import React from 'react'
import { Tooltip } from '@mui/material'
import { Search as SearchIcon, SaveOutlined, RestartAltRounded, CheckCircleRounded, WarningAmberRounded, UploadRounded, CloseRounded } from '@mui/icons-material'
import { api } from '../../function/axios'
import { ConfirmV2 } from '../ConfirmV2'
import { IconUploadDialog } from './IconUploadDialog'
import { IconTile } from '../source-icon/SourceIcon'
import { SourceIconSpec, SourceIconCategory, SourceIconStyle, reloadSourceIcons } from '../source-icon/sourceIconStore'
import s from './SystemV2.module.css'

/*
 * System → Interface → Source icons, in three collections — Text (the
 * coloured letter badges the result pages had; the default), Original (the
 * sprite icons, drawn bare) and Modern (tiles) — each edited on its own, one
 * of them active for the admin and the dashboard. The sources are listed by category,
 * in the categories' own order, then by name. Every source's icon: the standard set the
 * backend ships, or an admin's change stored in source_icons. The calls:
 * GET /source-icons/editor?collection=, PUT /source-icons/collection/active,
 * PUT /source-icons/:source?collection= (the whole icon),
 * DELETE /source-icons/:source (back to the standard one). Each change goes
 * to admin_audit; the admin's own icons reload at once, the dashboard picks it
 * up by the set's version.
 */

type Row = { source: string; category: string | null; standard: SourceIconSpec | null; custom: SourceIconSpec | null; updatedBy: string | null; updatedAt: string | null }
type Collection = { key: string; label: string; style: SourceIconStyle; active: boolean }
type Editor = { collections: Collection[]; collection: string; categories: SourceIconCategory[]; library: { name: string; svg: string }[]; sources: Row[] }
type Kind = 'svg' | 'logo' | 'text'
type BadgeKind = 'none' | 'svg' | 'text'
type Draft = { category: string; kind: Kind; svg: string; logo: string; text: string; badgeKind: BadgeKind; badge: string; label: string; color: string }

const MAX_LOGO = 150 * 1024
const errText = (e: any) => {
  const m = e?.response?.data?.message
  return (Array.isArray(m) ? m.join(', ') : typeof m === 'string' ? m : e?.message) || 'Something went wrong'
}
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '')

const draftOf = (spec: SourceIconSpec | null, fallbackCategory: string, look: SourceIconStyle = 'tile'): Draft => ({
  category: spec?.category || fallbackCategory,
  // a new icon starts as what its collection is made of: pictures (bare), letters (text) or glyphs
  kind: spec?.logo ? 'logo' : spec?.text ? 'text' : spec?.svg ? 'svg' : look === 'bare' ? 'logo' : look === 'text' ? 'text' : 'svg',
  svg: spec?.svg || '',
  logo: spec?.logo || '',
  text: spec?.text || '',
  badgeKind: !spec?.badge ? 'none' : spec.badge.startsWith('<') ? 'svg' : 'text',
  badge: spec?.badge || '',
  label: spec?.label || '',
  color: spec?.color || '#64748B',
})
const specOf = (d: Draft): SourceIconSpec => ({
  category: d.category,
  ...(d.kind === 'svg' && d.svg ? { svg: d.svg } : {}),
  ...(d.kind === 'logo' && d.logo ? { logo: d.logo } : {}),
  ...(d.kind === 'text' && d.text ? { text: d.text } : {}),
  ...(d.badgeKind !== 'none' && d.badge ? { badge: d.badge } : {}),
  ...(d.label.trim() ? { label: d.label.trim() } : {}),
  ...(d.kind === 'text' ? { color: d.color } : {}),
})
const problem = (d: Draft): string | null => {
  if (d.kind === 'svg' && !d.svg) return 'Pick a glyph or paste an SVG'
  if (d.kind === 'svg' && !/^<svg[\s>][\s\S]*<\/svg>$/.test(d.svg.trim())) return 'The SVG must start with <svg and end with </svg>'
  if (d.kind === 'logo' && !d.logo) return 'Upload a picture'
  if (d.kind === 'text' && !/^[A-Za-z0-9]{1,3}$/.test(d.text)) return 'One to three letters or digits'
  if (d.badgeKind === 'text' && !/^[A-Za-z0-9]{1,3}$/.test(d.badge)) return 'Badge: one to three letters or digits'
  if (d.badgeKind === 'svg' && !d.badge) return 'Pick a badge glyph'
  return null
}

export function SourceIconsV2() {
  const [data, setData] = React.useState<Editor | null>(null)
  const [note, setNote] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [query, setQuery] = React.useState('')
  const [show, setShow] = React.useState<'all' | 'changed'>('all')
  // opens on Phone apps — the first category; "Every category" shows them all
  const [cat, setCat] = React.useState('phone')
  const [open, setOpen] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState<Draft | null>(null)
  const [libQuery, setLibQuery] = React.useState('')
  const [picking, setPicking] = React.useState<'icon' | 'badge'>('icon')
  const [busy, setBusy] = React.useState(false)
  const [toReset, setToReset] = React.useState<string | null>(null)

  const load = React.useCallback((collection?: string) => {
    api({ method: 'GET', url: '/source-icons/editor', params: collection ? { collection } : undefined })
      .then(({ data }: any) => setData(data))
      .catch((e) => { setData({ collections: [], collection: '', categories: [], library: [], sources: [] }); setNote({ kind: 'err', text: errText(e) }) })
  }, [])
  React.useEffect(() => { load() }, [load])

  const current = data?.collections.find((c) => c.key === data.collection)
  const look: SourceIconStyle = current?.style || 'tile'
  const catOf = (r: Row) => effective(r)?.category ?? r.category

  function switchTo(key: string) {
    if (!data || key === data.collection) return
    setOpen(null)
    setDraft(null)
    setData(null)
    load(key)
  }

  async function activate() {
    if (!data) return
    setBusy(true)
    try {
      const { data: next }: any = await api({ method: 'PUT', url: '/source-icons/collection/active', body: { collection: data.collection } })
      setData(next)
      void reloadSourceIcons()
      setNote({ kind: 'ok', text: `${current?.label} is the active collection — the admin shows it now, the dashboard on its next load` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setBusy(false)
    }
  }

  const hue = (key?: string) => data?.categories.find((c) => c.key === key)?.hue
  const effective = (r: Row) => r.custom ?? r.standard
  const row = data?.sources.find((r) => r.source === open) || null

  function edit(r: Row) {
    setOpen(r.source)
    setDraft(draftOf(effective(r), r.category || data?.categories[0]?.key || '', look))
    setPicking('icon')
    setLibQuery('')
  }

  const q = query.trim().toLowerCase()
  const shown = (data?.sources || []).filter((r) =>
    (show === 'all' || r.custom) &&
    (!cat || catOf(r) === cat) &&
    (!q || r.source.toLowerCase().includes(q) || (effective(r)?.label || '').toLowerCase().includes(q)))
  const changedCount = (data?.sources || []).filter((r) => r.custom).length

  /* by category, in the categories' order; a source with no icon at all comes last */
  const groups = [
    ...(data?.categories || []).map((c) => ({ key: c.key, label: c.label, hue: c.hue as number | undefined, rows: shown.filter((r) => catOf(r) === c.key) })),
    { key: '', label: 'No category', hue: undefined as number | undefined, rows: shown.filter((r) => !data?.categories.some((c) => c.key === catOf(r))) },
  ]
    .map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => (effective(a)?.label || a.source).localeCompare(effective(b)?.label || b.source)) }))
    .filter((g) => g.rows.length)

  const lq = libQuery.trim().toLowerCase()
  const library = (data?.library || []).filter((l) => !lq || l.name.toLowerCase().includes(lq))

  /* the whole icon from the draft — or from `with`, the upload form's picture */
  async function save(withDraft?: Draft): Promise<boolean> {
    const d = withDraft || draft
    if (!open || !d) return false
    setBusy(true)
    try {
      const { data: next }: any = await api({ method: 'PUT', url: `/source-icons/${encodeURIComponent(open)}`, params: { collection: data?.collection }, body: specOf(d) })
      setData(next)
      if (withDraft) setDraft(withDraft)
      void reloadSourceIcons()
      setNote({ kind: 'ok', text: `Saved the ${open} icon — the dashboard shows it on its next load` })
      return true
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
      return false
    } finally {
      setBusy(false)
    }
  }

  async function reset(source: string) {
    setBusy(true)
    try {
      const { data: next }: any = await api({ method: 'DELETE', url: `/source-icons/${encodeURIComponent(source)}`, params: { collection: data?.collection } })
      setData(next)
      void reloadSourceIcons()
      const r = (next as Editor).sources.find((x) => x.source === source)
      if (r && open === source) setDraft(draftOf(effective(r), r.category || next.categories[0]?.key || '', look))
      setNote({ kind: 'ok', text: `${source} is back to its default icon` })
    } catch (e) {
      setNote({ kind: 'err', text: errText(e) })
    } finally {
      setBusy(false)
    }
  }

  const [uploading, setUploading] = React.useState(false)

  const set = (patch: Partial<Draft>) => setDraft((d) => d && { ...d, ...patch })
  const bad = draft ? problem(draft) : null
  const preview = draft ? specOf(draft) : null
  const previewHue = draft ? hue(draft.category) : undefined

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <b>Source icons</b>
          <span>The icon of every search source in the admin and the dashboard. Pick the collection the apps show; edit either one — a change stays in its collection, Reset puts the collection’s own icon back.</span>
        </div>
      </div>

      {data && data.collections.length > 0 && (
        <div className={s.toolbar}>
          <div className={s.seg} role="group" aria-label="Collection">
            {data.collections.map((c) => (
              <button key={c.key} type="button" className={c.key === data.collection ? s.segOn : ''} onClick={() => switchTo(c.key)}>
                {c.label}{c.active && <em>active</em>}
              </button>
            ))}
          </div>
          {current && !current.active && (
            <button type="button" className={s.primary} onClick={() => void activate()} disabled={busy}>
              Make {current.label} active
            </button>
          )}
          {current?.active && <span className={s.hint}>The admin and the dashboard show this collection.</span>}
        </div>
      )}

      {note && (
        <div className={`${s.notice} ${note.kind === 'err' ? s.noticeErr : s.noticeOk}`}>
          {note.kind === 'err' ? <WarningAmberRounded sx={{ fontSize: 17 }} /> : <CheckCircleRounded sx={{ fontSize: 17 }} />}
          <span>{note.text}</span>
          <button type="button" onClick={() => setNote(null)} aria-label="Close">×</button>
        </div>
      )}

      {data === null && <div className={s.empty}>Loading…</div>}

      {data && data.sources.length > 0 && (
        <div className={s.toolbar}>
          <div className={s.seg}>
            <button type="button" className={show === 'all' ? s.segOn : ''} onClick={() => setShow('all')}>All <em>{data.sources.length}</em></button>
            <button type="button" className={show === 'changed' ? s.segOn : ''} onClick={() => setShow('changed')}>Changed <em>{changedCount}</em></button>
          </div>
          <select className={s.siSelect} value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
            <option value="">Every category</option>
            {data.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <div className={s.search}>
            <SearchIcon sx={{ fontSize: 17 }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a source" />
          </div>
        </div>
      )}

      {data && data.sources.length > 0 && (
        <div className={`${s.siLayout} ${row ? s.siLayoutOpen : ''}`}>
          <div className={s.siGroups}>
            {groups.map((g) => (
              <div key={g.key} className={s.siGroup}>
                <div className={s.siGroupHead}>
                  <IconTile spec={null} hue={g.hue} size={12} />
                  <b>{g.label}</b>
                  <span className={s.muted}>{g.rows.length}</span>
                </div>
                <div className={s.siGrid}>
                  {g.rows.map((r) => {
                    const spec = effective(r)
                    return (
                      <button key={r.source} type="button" className={`${s.siCard} ${open === r.source ? s.siCardOn : ''}`} onClick={() => edit(r)}>
                        <IconTile spec={spec} hue={spec ? hue(spec.category) : undefined} look={spec ? look : 'tile'} size={28} />
                        <span className={s.siName}>
                          <b>{spec?.label || r.source}</b>
                          {spec?.label && <small>{r.source}</small>}
                        </span>
                        {r.custom && <span className={`${s.srcTag} ${s.srcDb}`}>Changed</span>}
                        {!r.standard && !r.custom && <span className={`${s.srcTag} ${s.srcNone}`}>No icon</span>}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            {shown.length === 0 && <div className={s.empty}>No source matches “{query.trim()}”</div>}
          </div>

          {row && draft && (
            <div className={`${s.card} ${s.siEditor}`}>
              <div className={s.cardHead}>
                <b>{row.source}</b>
                {row.custom
                  ? <span className={`${s.srcTag} ${s.srcDb}`}>Changed</span>
                  : <span className={`${s.srcTag} ${s.srcEnv}`}>Default</span>}
                <span className={s.spacer} />
                <button type="button" className={s.setReset} onClick={() => { setOpen(null); setDraft(null) }} aria-label="Close"><CloseRounded sx={{ fontSize: 17 }} /></button>
              </div>

              <div className={s.siPreview}>
                <div className={s.siSwatch}>
                  <IconTile spec={preview} hue={previewHue} look={look} size={44} mode="light" />
                  <IconTile spec={preview} hue={previewHue} look={look} size={20} mode="light" />
                </div>
                <div className={`${s.siSwatch} ${s.siSwatchDark}`}>
                  <IconTile spec={preview} hue={previewHue} look={look} size={44} mode="dark" />
                  <IconTile spec={preview} hue={previewHue} look={look} size={20} mode="dark" />
                </div>
                <span className={s.hint}>Light and dark, at the editor’s and the tables’ size</span>
              </div>

              <label className={s.setField}>
                <span className={s.setLabel}><b>Name shown</b></span>
                <input className={s.setInput} value={draft.label} placeholder={row.source} maxLength={60} onChange={(e) => set({ label: e.target.value })} />
              </label>

              <label className={s.setField}>
                <span className={s.setLabel}><b>Category</b><span className={s.hint}>sets the colour</span></span>
                <select className={s.setInput} value={draft.category} onChange={(e) => set({ category: e.target.value })}>
                  {data.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>

              <div className={s.setField}>
                <span className={s.setLabel}><b>Icon</b></span>
                <div className={s.seg}>
                  <button type="button" className={draft.kind === 'svg' ? s.segOn : ''} onClick={() => { set({ kind: 'svg' }); setPicking('icon') }}>Glyph</button>
                  <button type="button" className={draft.kind === 'logo' ? s.segOn : ''} onClick={() => set({ kind: 'logo' })}>Picture</button>
                  <button type="button" className={draft.kind === 'text' ? s.segOn : ''} onClick={() => set({ kind: 'text' })}>Letters</button>
                </div>
                {draft.kind === 'logo' && (
                  <button type="button" className={s.ghostBtn} style={{ alignSelf: 'flex-start' }} onClick={() => setUploading(true)}>
                    <UploadRounded sx={{ fontSize: 16 }} />{draft.logo ? 'Replace picture' : 'Upload picture'}
                  </button>
                )}
                {draft.kind === 'logo' && <span className={s.hint}>PNG, JPEG or WebP, up to 150 KB. Square, about 60×60, looks best.</span>}
                {draft.kind === 'text' && <input className={s.setInput} value={draft.text} maxLength={3} placeholder="Ab" onChange={(e) => set({ text: e.target.value })} />}
                {draft.kind === 'text' && look === 'text' && (
                  <span className={s.siColor}>
                    <input type="color" value={draft.color} onChange={(e) => set({ color: e.target.value })} aria-label="Badge colour" />
                    <span className={s.hint}>Badge colour {draft.color.toUpperCase()}</span>
                  </span>
                )}
              </div>

              <div className={s.setField}>
                <span className={s.setLabel}><b>Corner badge</b></span>
                <div className={s.seg}>
                  <button type="button" className={draft.badgeKind === 'none' ? s.segOn : ''} onClick={() => set({ badgeKind: 'none', badge: '' })}>None</button>
                  <button type="button" className={draft.badgeKind === 'svg' ? s.segOn : ''} onClick={() => { set({ badgeKind: 'svg', badge: draft.badge.startsWith('<') ? draft.badge : '' }); setPicking('badge') }}>Glyph</button>
                  <button type="button" className={draft.badgeKind === 'text' ? s.segOn : ''} onClick={() => set({ badgeKind: 'text', badge: draft.badge.startsWith('<') ? '' : draft.badge })}>Letters</button>
                </div>
                {draft.badgeKind === 'text' && <input className={s.setInput} value={draft.badge} maxLength={3} placeholder="API" onChange={(e) => set({ badge: e.target.value })} />}
              </div>

              {((draft.kind === 'svg' && picking === 'icon') || (draft.badgeKind === 'svg' && picking === 'badge')) && (
                <div className={s.setField}>
                  <span className={s.setLabel}>
                    <b>{picking === 'icon' ? 'Pick the glyph' : 'Pick the badge'}</b>
                    <span className={s.spacer} />
                    {draft.kind === 'svg' && draft.badgeKind === 'svg' && (
                      <button type="button" className={s.siLink} onClick={() => setPicking(picking === 'icon' ? 'badge' : 'icon')}>
                        {picking === 'icon' ? 'Pick the badge instead' : 'Pick the glyph instead'}
                      </button>
                    )}
                  </span>
                  <div className={s.search} style={{ marginLeft: 0 }}>
                    <SearchIcon sx={{ fontSize: 17 }} />
                    <input value={libQuery} onChange={(e) => setLibQuery(e.target.value)} placeholder="Find a glyph: mail, map, users…" />
                  </div>
                  <div className={s.siLib}>
                    {library.map((l) => {
                      const on = picking === 'icon' ? draft.svg === l.svg : draft.badge === l.svg
                      return (
                        <Tooltip key={l.name} title={l.name}>
                          <button type="button" className={`${s.siLibItem} ${on ? s.siLibOn : ''}`} aria-label={l.name}
                            onClick={() => set(picking === 'icon' ? { kind: 'svg', svg: l.svg } : { badgeKind: 'svg', badge: l.svg })}>
                            <span dangerouslySetInnerHTML={{ __html: l.svg }} />
                          </button>
                        </Tooltip>
                      )
                    })}
                  </div>
                  {picking === 'icon' && (
                    <details className={s.siDetails}>
                      <summary>Paste your own SVG</summary>
                      <textarea className={s.siTextarea} value={draft.svg} spellCheck={false} placeholder='<svg viewBox="0 0 24 24" …>…</svg>'
                        onChange={(e) => set({ kind: 'svg', svg: e.target.value })} />
                      <span className={s.hint}>24×24, drawn in currentColor so it follows the theme. Shapes and paths only — scripts, links, styles and text are refused.</span>
                    </details>
                  )}
                </div>
              )}

              {bad && <span className={s.err}>{bad}</span>}
              {row.custom && row.updatedAt && <span className={s.hint}>Changed by {row.updatedBy || 'an admin'}, {when(row.updatedAt)}</span>}

              <div className={s.editRow}>
                <button type="button" className={s.primary} onClick={() => void save()} disabled={!!bad || busy}>
                  <SaveOutlined sx={{ fontSize: 16 }} />{busy ? 'Saving…' : 'Save'}
                </button>
                {row.custom && (
                  <button type="button" className={s.ghostBtn} onClick={() => setToReset(row.source)} disabled={busy}>
                    <RestartAltRounded sx={{ fontSize: 16 }} />{row.standard ? 'Reset to default' : 'Remove'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {uploading && row && draft && (() => {
        const now = effective(row)
        const withPicture = (src: string): Draft => ({ ...draft, kind: 'logo', logo: src })
        const tiles = (spec: SourceIconSpec | null) => (
          <div className={s.siPreview}>
            <div className={s.siSwatch}>
              <IconTile spec={spec} hue={spec ? hue(spec.category) : undefined} look={spec ? look : 'tile'} size={44} mode="light" />
              <IconTile spec={spec} hue={spec ? hue(spec.category) : undefined} look={spec ? look : 'tile'} size={20} mode="light" />
            </div>
            <div className={`${s.siSwatch} ${s.siSwatchDark}`}>
              <IconTile spec={spec} hue={spec ? hue(spec.category) : undefined} look={spec ? look : 'tile'} size={44} mode="dark" />
              <IconTile spec={spec} hue={spec ? hue(spec.category) : undefined} look={spec ? look : 'tile'} size={20} mode="dark" />
            </div>
          </div>
        )
        return (
          <IconUploadDialog
            title={`${now?.logo ? 'Replace' : 'Upload'} the ${row.source} picture`}
            current={tiles(now)}
            currentSrc={now?.logo || null}
            custom={!!row.custom}
            types={['png', 'jpeg', 'webp']}
            accept="image/png,image/jpeg,image/webp"
            maxBytes={MAX_LOGO}
            rules="PNG, JPEG or WebP · up to 150 KB · square, about 60×60"
            preview={(src) => tiles(specOf(withPicture(src)))}
            busy={busy}
            onSave={(src) => { void save(withPicture(src)).then((ok) => ok && setUploading(false)) }}
            onReset={() => { setUploading(false); setToReset(row.source) }}
            onClose={() => setUploading(false)}
          />
        )
      })()}

      {toReset && (
        <ConfirmV2
          header={`Reset the ${toReset} icon?`}
          yesLabel="Reset"
          onYes={() => { const x = toReset; setToReset(null); void reset(x) }}
          onClose={() => setToReset(null)}
        >
          The changed icon is removed from this collection and its own icon is shown again.
        </ConfirmV2>
      )}
    </div>
  )
}
